import React, { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import { Mic, StopCircle, Phone, PhoneOff, Clock } from 'lucide-react';
import dashboardService from '../api';

const TranscriptDisplay = ({ onTranscriptionUpdate, onCallStatusChange }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [callStatus, setCallStatus] = useState('idle'); // idle | active | ended
  const [persistentLines, setPersistentLines] = useState([]);
  const [interimLine, setInterimLine] = useState('');
  const [callDuration, setCallDuration] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');

  const transcriptionEndRef = useRef(null);
  const transcriptionTextRef = useRef('');
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const processorRef = useRef(null);
  const socketRef = useRef(null);
  const callTimerRef = useRef(null);
  const updateIntervalRef = useRef(null);
  const lastSpeakerRef = useRef('Agent'); // first speaker is always the agent
  const lastLineTimeRef = useRef(null);   // ms timestamp of last finalised line

  // Auto-scroll
  useEffect(() => {
    if (transcriptionEndRef.current) {
      transcriptionEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [persistentLines, interimLine]);

  // Call duration timer
  useEffect(() => {
    if (isRecording) {
      callTimerRef.current = setInterval(() => {
        setCallDuration(d => d + 1);
      }, 1000);
    } else {
      clearInterval(callTimerRef.current);
    }
    return () => clearInterval(callTimerRef.current);
  }, [isRecording]);

  // Socket setup
  useEffect(() => {
    const socketUrl = dashboardService.getTranscriptionSocketUrl();
    socketRef.current = io(socketUrl, {
      path: '/socket.io/',
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5
    });

    const socket = socketRef.current;

    socket.on('transcription', (data) => {
      if (!data || !data.text) return;
      transcriptionTextRef.current += `\n${data.text}`;

      if (data.text.startsWith('##')) {
        const cleanLine = data.text.replace(/^##\s*/, '');
        const now = Date.now();
        const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
        const speaker = resolveSpeaker(cleanLine, now);
        lastSpeakerRef.current = speaker;
        lastLineTimeRef.current = now;
        setPersistentLines(prev => [...prev, { text: cleanLine, timestamp, speaker }]);
        setInterimLine('');
      } else if (data.text.startsWith('>>')) {
        setInterimLine(data.text.replace(/^>>\s*/, ''));
      }
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    return () => socket.disconnect();
  }, []);

  // Speaker resolution: keyword match → question/narrative pattern → timing fallback
  const resolveSpeaker = (text, nowMs) => {
    const lower = text.toLowerCase().trim();

    // ── Strong agent markers ────────────────────────────────────────────────
    const agentPhrases = [
      // Opening / greeting
      'thank you for calling', 'thanks for calling', 'how can i help', 'how may i help',
      'how can i assist', 'how may i assist', 'my name is', 'this is michael', 'this is sarah',
      'this is angela', 'member services', 'how may i assist you today',
      // Empathy (agent-specific phrasing — NOT bare "i understand" which customers also say)
      "i'm sorry to hear", "i'm sorry that happened", 'i am sorry to hear',
      'i understand your', 'i understand the frustration', 'i apologize',
      'that must be', 'i can imagine',
      // Acknowledgement / transition (agent only)
      'understood', "let's talk about", 'let me ask you', "i'll ask you",
      'one moment', 'bear with me', 'just a moment',
      // Procedural / intake
      'let me check', 'let me pull up', 'let me look into', 'let me verify',
      'can you verify', 'can you confirm', 'could you confirm', 'can you provide',
      'could you provide', 'can you tell me', 'could you tell me',
      'pulling up your account', 'i can see here', 'i can see on my end',
      "i'll go ahead", "i'll look into", "i'll check", "i'll note", "i'll document",
      "i'll file", "i'll escalate", "i'll transfer", "i'll send you", "i'll provide",
      "i'll open", "i'll create", "i'll initiate", 'reference number', 'case number',
      'claim number', 'ticket number', 'on your behalf', 'on our end', 'from our side',
      // Fraud intake questions
      'did you authorize', 'did you personally authorize', 'did you recognize',
      'did the seller', 'did someone contact', 'did you receive', 'did you provide',
      'did you share', 'did you click', 'how did you', 'how did you 1st',
      'when did this happen', 'when did you notice', 'how much was', 'what was the amount',
      'what was the date', 'which merchant', 'which platform', 'were there additional',
      'and were there', 'how did you send', 'how did you pay',
      // Procedural intake statements (agent explains the process)
      'as part of our', 'as part of the', 'for our records', 'for verification purposes',
      'one key step', 'key step in our', 'intake process', 'in our intake',
      'we need to determine', 'we need to verify', 'determining whether',
      'unauthorized fraud or', 'authorized payment induced',
      // Classification / analysis
      'this falls under', 'this is classified', 'this appears to be', 'this would be',
      'we classify this', 'we classify', 'classify this under', 'classified as',
      'based on what you', 'given that you', 'since you authorized', 'since you sent',
      'because you authorized', 'but were misled', 'federal reserve', 'fraud classifier',
      'p2p payment', 'authorized push', 'investment scam', 'marketplace scam',
      // Actions / next steps
      'we will initiate', 'we will open', 'we will file', 'we will process',
      'we will investigate', "we'll investigate", "we'll open", "we'll file",
      'initiate a case', 'open a case', 'open a dispute', 'card dispute',
      'fraud and scams team', 'ach dispute', 'wire transfer dispute',
      'i recommend', 'you should also', 'going forward', 'next steps',
      "i'll provide guidance", 'i will provide guidance', 'guidance on',
      'thank you for', // "thank you for [sharing/calling/your patience]" — agent usage
    ];

    // ── Strong customer markers ─────────────────────────────────────────────
    const customerPhrases = [
      'customer:', 'caller:',
      // Reporting the problem
      'i got scammed', 'i was scammed', "i've been scammed", 'i think i got',
      'i think i may have', 'i may have been scammed', 'i was defrauded',
      'someone stole', 'i was robbed', 'i was a victim',
      'i want to report', 'i need to report', 'i need help', 'i have a problem',
      // Authorization (first-person denial — NOT the agent describing it)
      'i did not authorize', "i didn't authorize",
      "i didn't do this", "i didn't make this", 'i never authorized',
      'i never made', 'i never sent', 'i never received',
      'they never sent', 'they never delivered',
      "they're not responding", 'they stopped responding', 'they blocked me',
      // Payment apps / platforms
      'zelle', 'venmo', 'cash app', 'paypal', 'apple pay', 'google pay', 'whatsapp',
      'facebook marketplace', 'craigslist', 'concert ticket', 'taylor swift',
      'crypto', 'bitcoin', 'investment platform', 'trading platform',
      'i sent', 'i paid', 'i transferred', 'i wired', 'i deposited',
      'i used my debit card', 'i used my credit card', 'using my debit', 'using my credit',
      'they asked me to pay', 'they told me to send', 'they told me to pay',
      'they asked me to use', 'they wanted me to', 'to unlock my',
      // Narrating what happened
      'i found someone', 'i saw an ad', 'i saw a listing', 'i met someone', 'i bought',
      'i came into contact', 'i first came', 'i 1st came', 'social media', 'online ad',
      'they said', 'they told me', 'they asked me', 'they promised', 'they instructed',
      'i thought they were', 'i believed they were', 'i believed it was',
      'because i believed', 'i thought it was real',
      'last minute', 'at the last minute', 'they changed',
      // Account / card
      'my account', 'my card', 'my bank', 'my money', 'my funds', 'my debit',
      "i didn't", "i can't", "i wasn't", "i haven't", 'i noticed', 'i checked',
      'i received a text', 'i received an email', 'i received a call',
      'someone called me', 'someone emailed me', 'someone texted me',
      // Short customer confirmations after agent questions
      'yes they', 'no they', 'yes i did', 'no i did',
      'yes because', 'mostly', 'mostly whatsapp', // "Mostly WhatsApp and email"
      'i understand', // bare "I understand" = customer confirming they follow agent's explanation
      'i get it', 'okay', 'alright', 'sure', 'right', // short acknowledgements → customer
    ];

    if (agentPhrases.some(p => lower.includes(p))) return 'Agent';
    if (customerPhrases.some(p => lower.includes(p))) return 'Customer';

    // ── Pattern-based heuristics ────────────────────────────────────────────
    const wordCount = lower.split(/\s+/).filter(Boolean).length;

    // Questions are almost always the agent probing the customer
    const isQuestion = lower.endsWith('?');
    if (isQuestion) return 'Agent';

    // Very short lines (≤ 5 words) → customer one-word / short acknowledgement
    // (catches "Okay.", "Right.", "I see.", "Ch." (truncated), bare "Yes." / "No.")
    if (wordCount <= 5) return 'Customer';

    // Long narrative (> 15 words) while agent was last → customer describing the scam
    if (wordCount > 15 && lastSpeakerRef.current === 'Agent') return 'Customer';

    // ── Timing fallback: only switch after a meaningful pause (3 s) ─────────
    if (lastLineTimeRef.current !== null && nowMs - lastLineTimeRef.current > 3000) {
      return lastSpeakerRef.current === 'Agent' ? 'Customer' : 'Agent';
    }

    return lastSpeakerRef.current;
  };

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleStartCall = useCallback(async () => {
    if (isRecording) return;

    setIsRecording(true);
    setCallStatus('active');
    setCallDuration(0);
    setPersistentLines([]);
    setInterimLine('');
    transcriptionTextRef.current = '';
    lastSpeakerRef.current = 'Agent';
    lastLineTimeRef.current = null;
    if (onCallStatusChange) onCallStatusChange('active');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      await audioContext.audioWorklet.addModule('/audio-processor.js');
      const source = audioContext.createMediaStreamSource(stream);
      const workletNode = new AudioWorkletNode(audioContext, 'audio-processor');

      workletNode.port.onmessage = (event) => {
        try {
          const inputData = event.data.audioData;
          const downsampled = dashboardService.downsampleBuffer(inputData, audioContext.sampleRate, 16000);
          const pcmBuffer = dashboardService.floatTo16BitPCM(downsampled);
          socketRef.current.emit('audio_stream', new Uint8Array(pcmBuffer));
        } catch (err) {
          console.error('Audio processing error:', err);
        }
      };

      source.connect(workletNode);
      workletNode.connect(audioContext.destination);
      processorRef.current = workletNode;
    } catch (error) {
      console.error('Microphone access error:', error);
      setIsRecording(false);
      setCallStatus('idle');
    }

    // Periodically push transcript updates to parent
    updateIntervalRef.current = setInterval(() => {
      const currentText = transcriptionTextRef.current + (interimLine ? `\n${interimLine}` : '');
      if (currentText.trim() && onTranscriptionUpdate) {
        onTranscriptionUpdate(currentText);
      }
      if (socketRef.current && currentText.trim()) {
        socketRef.current.emit('context_update', { text: currentText });
      }
    }, 3000);
  }, [isRecording, interimLine, onTranscriptionUpdate, onCallStatusChange]);

  const handleEndCall = useCallback(async () => {
    if (!isRecording) return;

    setIsRecording(false);
    setCallStatus('ended');
    clearInterval(updateIntervalRef.current);

    if (socketRef.current) socketRef.current.emit('stop_audio');
    if (interimLine.trim()) transcriptionTextRef.current += `\n${interimLine}`;

    const finalText = transcriptionTextRef.current;

    try {
      if (processorRef.current) {
        processorRef.current.port.postMessage({ terminate: true });
        processorRef.current.disconnect();
        processorRef.current = null;
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(t => t.stop());
        mediaStreamRef.current = null;
      }
      if (audioContextRef.current) {
        await audioContextRef.current.close();
        audioContextRef.current = null;
      }
    } catch (e) {
      console.error('Cleanup error:', e);
    }

    if (onTranscriptionUpdate && finalText.trim()) onTranscriptionUpdate(finalText);
    if (onCallStatusChange) onCallStatusChange('ended');
  }, [isRecording, interimLine, onTranscriptionUpdate, onCallStatusChange]);

  // Agent   → deep navy  #000080
  // Customer → steel blue #0369a1
  const SPEAKER_STYLES = {
    Agent:    { color: '#000080', bg: 'rgba(0,0,128,0.06)',  border: 'rgba(0,0,128,0.12)'  },
    Customer: { color: '#0369a1', bg: 'rgba(3,105,161,0.06)', border: 'rgba(3,105,161,0.12)' },
  };

  const getSpeakerStyle = (speaker) =>
    ({ color: SPEAKER_STYLES[speaker]?.color ?? '#6b7280' });

  const getSpeakerBgStyle = (speaker) => ({
    backgroundColor: SPEAKER_STYLES[speaker]?.bg ?? 'rgba(0,0,0,0.03)',
    borderLeft: `3px solid ${SPEAKER_STYLES[speaker]?.border ?? 'transparent'}`,
  });

  return (
    <div className="flex flex-col h-full min-h-0 bg-white rounded-xl shadow-md shadow-blue-900/5 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-[#000080]/5 to-[#000080]/10">
        <div className="flex items-center gap-2">
          <Phone size={16} style={{ color: '#000080' }} />
          <h2 className="font-semibold text-gray-800 text-sm">Live Transcription</h2>
        </div>
        <div className="flex items-center gap-3">
          {isRecording && (
            <div className="flex items-center gap-1.5 text-xs text-red-600 font-medium">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <Clock size={12} />
              {formatDuration(callDuration)}
            </div>
          )}
          {callStatus === 'ended' && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              Call Ended
            </span>
          )}
        </div>
      </div>

      {/* Transcript area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2 min-h-0">
        {persistentLines.length === 0 && !interimLine && (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
            <Mic size={32} className="opacity-30" />
            <p className="text-sm">Start the call to see live transcription</p>
          </div>
        )}

        {persistentLines.map((line, i) => (
          <div key={i} className="rounded-lg px-3 py-2" style={getSpeakerBgStyle(line.speaker)}>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-xs font-semibold" style={getSpeakerStyle(line.speaker)}>
                {line.speaker}
              </span>
              <span className="text-xs text-gray-400">{line.timestamp}</span>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">{line.text}</p>
          </div>
        ))}

        {interimLine && (
          <div className="rounded-lg px-3 py-2 border border-dashed"
            style={{ backgroundColor: 'rgba(0,0,128,0.03)', borderColor: 'rgba(0,0,128,0.18)', borderLeft: '3px solid rgba(0,0,128,0.25)' }}>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-xs font-semibold" style={{ color: '#000080' }}>Speaking…</span>
            </div>
            <p className="text-sm italic leading-relaxed text-gray-500">{interimLine}</p>
          </div>
        )}

        <div ref={transcriptionEndRef} />
      </div>

      {/* Upload status */}
      {uploadStatus && (
        <div className="px-4 py-1 text-xs text-gray-500 bg-gray-50 border-t border-gray-100">
          {uploadStatus}
        </div>
      )}

      {/* Controls */}
      <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
        <div className="flex gap-2">
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
            callStatus === 'active' ? 'bg-green-100 text-green-700' :
            callStatus === 'ended' ? 'bg-gray-100 text-gray-600' :
            'bg-gray-100 text-gray-500'
          }`}>
            {callStatus === 'active' ? 'Active' : callStatus === 'ended' ? 'Ended' : 'Idle'}
          </span>
        </div>
        <div className="flex gap-2">
          {!isRecording ? (
            <button
              onClick={handleStartCall}
              className="flex items-center gap-1.5 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors hover:opacity-90"
              style={{ background: '#000080' }}
            >
              <Phone size={13} />
              Start Call
            </button>
          ) : (
            <button
              onClick={handleEndCall}
              className="flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
            >
              <PhoneOff size={13} />
              End Call
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TranscriptDisplay;
